import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./ProfileV5.css";

// Wire-shape returned by GET /api/wall/:handle
interface ApiTile {
  id: string;
  name: string;
  message: string;
  style: TileStyle;
  color: string;
  x_pct: number;
  y_pct: number;
  monthly_cents: number;
  founder_number: number | null;
  anniversary_months: number;
  fade_started_at: number | null;
  created_at: number;
  creator_reply: string | null;
  creator_reply_at: number | null;
  emoji: string | null;
}
interface ApiCreator {
  handle: string;
  display_name: string;
  bio: string | null;
  avatar_url: string | null;
  youtube_handle: string | null;
  youtube_channel_id: string | null;
  is_creator: boolean;
  member_since_ms: number | null;
  wall_theme: string | null;
  wall_bg_url: string | null;
  wall_bg_opacity: number | null;
  wall_font: string | null;
  wall_accent_color: string | null;
}
interface ApiResp {
  source: "mock" | "live";
  creator?: ApiCreator;
  tiles?: ApiTile[];
}

function apiTileToSubTile(t: ApiTile): SubTile {
  return {
    id: t.id,
    name: t.name,
    message: t.message,
    style: t.style,
    color: t.color,
    xPct: t.x_pct,
    yPct: t.y_pct,
    monthlyCents: t.monthly_cents,
    anniversaryMonths: t.anniversary_months,
    founderNumber: t.founder_number,
    boostUntil: null,
    fadeStartedAt: t.fade_started_at,
    badge: null,
    blink: t.style === "neon" && t.monthly_cents >= 1000,
    creatorReply: t.creator_reply,
    creatorReplyAt: t.creator_reply_at,
    emoji: t.emoji,
  };
}

function formatMonthYear(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString("en-US", { month: "short", year: "numeric" });
}

function mockProfile(handle: string) {
  const display = handle.charAt(0).toUpperCase() + handle.slice(1);
  // Six tiles + explicit colors matching the reference exactly.
  // Reference image (5d94b180…) is the canonical spec — days, dollar amounts,
  // and colors are read from it pixel-by-pixel. No emojis on the tiles per the
  // image (text-only neon, name + message + footer).
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle; color: string; daysSubscribed?: number; anniversaryMonths?: number; founderNumber?: number | null }> = [
    { name: "KAIROS",     message: "build time is shipping forever.",        monthlyCents: 500,   style: "neon", color: "#ffea00", daysSubscribed: 7 },                // yellow
    { name: "ZINC",       message: "Extra for a forever cert",               monthlyCents: 600,   style: "neon", color: "#e8e8f5", daysSubscribed: 7 },                // silver/white
    { name: "PULSE",      message: "tell my main I made it on the wall",     monthlyCents: 100,   style: "neon", color: "#ff3b30", daysSubscribed: 7 },                  // red
    { name: "VEXLINE",    message: "watching since the API days",            monthlyCents: 5600,  style: "neon", color: "#8a8a96", daysSubscribed: 112 },                  // mid-gray
    { name: "AURORA",     message: "day-one founder. forever.",              monthlyCents: 25000, style: "neon", color: "#ffc73b", daysSubscribed: 367, anniversaryMonths: 12, founderNumber: 1 }, // GOLD
    { name: "LO-FI-GHOST",message: "playing your VOD on loop while I code",  monthlyCents: 10000, style: "neon", color: "#00e1ff", daysSubscribed: 40 },                   // cyan
  ];
  const tiles: SubTile[] = seed.map((s, i) => {
    // Compressed positions per Gemini diff — tight vertical rhythm.
    //   Row 1: KAIROS, ZINC, PULSE (small donor row, y≈15)
    //   Row 2: VEXLINE left, AURORA right anchor (y≈50)
    //   Row 3: LO-FI-GHOST under VEXLINE (y≈80)
    // Pixel-targeted positions to match the 5d94b180… reference image:
    //   Row 1 (top, y≈14): KAIROS, ZINC, PULSE — small donor row
    //   Row 2 (mid, y≈48): VEXLINE left, AURORA right (huge, spans rows 2-3)
    //   Row 3 (bottom, y≈78): LO-FI-GHOST left-of-center
    // AURORA is at xPct 73 so its bbox (hx≈22) lands flush with the right
    // edge while leaving 0-50% clear for the left column.
    const positions: Array<[number, number]> = [
      [15, 14], [40, 14], [83, 14],   // 0-2: KAIROS, ZINC, PULSE
      [22, 46], [68, 56],             // 3-4: VEXLINE, AURORA
      [22, 82],                       // 5: LO-FI-GHOST
    ];
    const [xPct, yPct] = positions[i] ?? [50, 50];
    return {
      id: `t${i}`,
      name: s.name,
      message: s.message,
      style: s.style,
      color: s.color ?? STYLE_PALETTES[s.style][(i + 2) % 8],
      xPct, yPct,
      monthlyCents: s.monthlyCents,
      anniversaryMonths: s.anniversaryMonths ?? 0,
      daysSubscribed: s.daysSubscribed,
      founderNumber: s.founderNumber ?? null,
      boostUntil: null,
      fadeStartedAt: null,
      badge: s.badge ?? null,
      blink: s.blink ?? false,
      emoji: null,
    };
  });
  return {
    handle,
    display_name: display,
    bio: "Long-form essays and interviews about the craft of building. Two videos a month, no filler.",
    youtube_handle: `@${handle}`,
    youtube_url: `https://youtube.com/@${handle}`,
    member_since: "Jun 2023",
    supporter_count: tiles.length,
    monthly_recurring: tiles.reduce((s, t) => s + t.monthlyCents / 100, 0),
    tiles,
  };
}

export default function ProfileV5(): JSX.Element {
  const { handle = "demo" } = useParams<{ handle: string }>();
  const [params] = useSearchParams();
  const signedTileId = params.get("signed");
  const [searchMatch, setSearchMatch] = useState<string | null>(null);
  // Search wins over the post-signup spotlight (user explicitly looking).
  const spotlightTileId = searchMatch ?? signedTileId;

  const mock = useMemo(() => mockProfile(handle), [handle]);
  const [live, setLive] = useState<ApiResp | null>(null);
  // Signed-in user's own handle, used to decide whether to show creator-only
  // controls (reply composer). Anonymous → empty string, no controls.
  const [myHandle, setMyHandle] = useState<string>("");

  const refreshLive = (): void => {
    fetch(`/api/wall/${encodeURIComponent(handle)}`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: ApiResp | null) => { if (j) setLive(j); })
      .catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/wall/${encodeURIComponent(handle)}`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: ApiResp | null) => { if (!cancelled && j) setLive(j); })
      .catch(() => {});
    fetch("/api/me/dashboard", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { signed_in?: boolean; user?: { handle: string } } | null) => {
        if (!cancelled && j?.signed_in && j.user) setMyHandle(j.user.handle);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [handle]);

  const isOwner = !!myHandle && myHandle === handle;

  // Merge live data over the mock baseline so the page always has something
  // to render. live=null → pure mock. live.source=mock → keep mock.
  const useLive = live && live.source === "live" && live.creator;
  const profile = useLive
    ? {
        handle: live.creator!.handle,
        display_name: live.creator!.display_name || mock.display_name,
        bio: live.creator!.bio || mock.bio,
        youtube_handle: live.creator!.youtube_handle || mock.youtube_handle,
        youtube_url: live.creator!.youtube_channel_id
          ? `https://youtube.com/channel/${live.creator!.youtube_channel_id}`
          : mock.youtube_url,
        member_since: live.creator!.member_since_ms
          ? formatMonthYear(live.creator!.member_since_ms)
          : mock.member_since,
        supporter_count: (live.tiles ?? []).filter((t) => !t.fade_started_at).length,
        monthly_recurring: (live.tiles ?? [])
          .filter((t) => !t.fade_started_at)
          .reduce((s, t) => s + t.monthly_cents / 100, 0),
        tiles: (live.tiles ?? []).map(apiTileToSubTile),
      }
    : mock;

  return (
    <div className="hy-page pv5">
      <nav className="pv5-nav hy-container">
        <Link to="/" className="pv5-brand">
          <span className="pv5-brand-glyph"></span> SubsWall
        </Link>
        <Link to={`/${handle}/sign`} className="hy-btn hy-btn-primary hy-btn-sm">
          Sign the wall
        </Link>
      </nav>

      {/* MASTHEAD — centered, no card, no sticker, no hand-drawn lines */}
      <header className="pv5-mast hy-container">
        <div className="pv5-av">{profile.display_name[0]}</div>
        <h1 className="hy-display pv5-h1">{profile.display_name}</h1>
        <a className="pv5-channel" href={profile.youtube_url} target="_blank" rel="noopener noreferrer">
          {profile.youtube_handle} on YouTube ↗
        </a>
        <p className="hy-lead pv5-bio">{profile.bio}</p>
        <div className="pv5-stats">
          <div className="pv5-stat">
            <div className="pv5-stat-num hy-mono">{profile.supporter_count}</div>
            <div className="pv5-stat-lbl">supporters</div>
          </div>
          <div className="pv5-stat">
            <div className="pv5-stat-num hy-mono">${profile.monthly_recurring.toLocaleString()}</div>
            <div className="pv5-stat-lbl">monthly</div>
          </div>
          <div className="pv5-stat">
            <div className="pv5-stat-num hy-mono pv5-stat-num-sm">{profile.member_since}</div>
            <div className="pv5-stat-lbl">since</div>
          </div>
        </div>
      </header>

      {/* Spotlight banner — appears right after a fan signs the wall.
          Auto-scrolls + auto-hides; supporter can share the link. */}
      {spotlightTileId ? (
        <SpotlightBanner
          tileId={spotlightTileId}
          handle={handle}
          displayName={profile.display_name}
        />
      ) : null}

      {/* Goal bar — public progress, dead-cheap urgency mechanic */}
      <GoalBar current={profile.supporter_count} goal={pickGoal(profile.supporter_count)} />

      {/* WALL — the page's hero asset, framed with header */}
      <section className="pv5-wall hy-container" id="pv5-wall-anchor">
        <div className="pv5-wall-header">
          <h2 className="pv5-wall-title">Wall of Signatures</h2>
        </div>
        <div className="pv5-wall-frame">
          <SubsWall
            tiles={profile.tiles}
            theme={(useLive && live!.creator!.wall_theme as "neon" | "atelier" | "graffiti" | "glow") || "neon"}
            spotlightTileId={spotlightTileId}
            autoGrow
            bgImageUrl={useLive ? live!.creator!.wall_bg_url : null}
            bgImageOpacity={useLive ? live!.creator!.wall_bg_opacity : null}
            customFont={useLive ? live!.creator!.wall_font : null}
            customAccent={useLive ? live!.creator!.wall_accent_color : null}
          />
          {profile.tiles.length === 0 ? (
            <div className="pv5-wall-empty">
              <div className="pv5-wall-empty-card">
                <div className="pv5-wall-empty-h">A blank wall waiting for its first name.</div>
                <div className="pv5-wall-empty-sub">
                  Be the first to sign — your tile is permanent.
                </div>
                <Link to={`/${handle}/sign`} className="hy-btn hy-btn-primary hy-btn-lg pv5-wall-empty-cta">
                  Sign the wall — $5/mo
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* CTA — single sentence, single button, no card */}
      <section className="pv5-cta hy-container">
        <h2 className="hy-display pv5-h2">
          Sign {profile.display_name}'s wall.
        </h2>
        <p className="hy-lead pv5-cta-lead">
          Pick an amount, write a line, drop your tile. Stay as long as you support.
        </p>
        <Link to={`/${handle}/sign`} className="hy-btn hy-btn-primary hy-btn-lg">
          Become a supporter
        </Link>
      </section>

      {/* Find-my-signature — sits between the CTA and the supporter list */}
      <WallSearch tiles={profile.tiles} onMatch={setSearchMatch} />

      {/* RECENT SUPPORTERS — creator gets inline reply composers here */}
      <section className="pv5-list hy-container">
        <h2 className="hy-display pv5-h2 pv5-h2-left">Recent supporters</h2>
        <ul className="pv5-supporters">
          {profile.tiles.slice(0, 10).map((t) => (
            <SupporterRow key={t.id} tile={t} handle={handle} isOwner={isOwner} onReplied={refreshLive} />
          ))}
        </ul>
      </section>

      {/* Self-billboard — tasteful, not aggressive */}
      <section className="pv5-poweredby hy-container">
        <Link to="/" className="pv5-poweredby-card">
          <span className="pv5-poweredby-glyph"></span>
          <div className="pv5-poweredby-body">
            <div className="pv5-poweredby-h">Like this wall?</div>
            <div className="pv5-poweredby-sub">
              Start your own at <strong>subswall.com</strong> — 60 sec, 4.9% flat fee.
            </div>
          </div>
          <span className="pv5-poweredby-cta">Start my wall →</span>
        </Link>
      </section>

      <footer className="pv5-footer">
        <div className="hy-container pv5-footer-inner">
          <div className="pv5-brand"><span className="pv5-brand-glyph"></span> SubsWall</div>
          <Link to="/">Back home</Link>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spotlight banner — shown on /:handle?signed=<tile-id> right after a fan
// signs the wall. Scrolls to the wall, lets the supporter copy a shareable
// "find my tile" link. Auto-dismisses after 12s.
// ---------------------------------------------------------------------------
function SpotlightBanner(props: { tileId: string; handle: string; displayName: string }): JSX.Element | null {
  const [visible, setVisible] = useState(true);
  const [copied, setCopied] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    // Tiny delay so the wall has time to mount + position tiles before scroll.
    const t1 = window.setTimeout(() => {
      const el = document.getElementById("pv5-wall-anchor");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    const t2 = window.setTimeout(() => setVisible(false), 12_000);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, []);

  if (!visible) return null;

  const shareUrl = `${window.location.origin}/${props.handle}?signed=${props.tileId}`;

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Older browsers fall back to a prompt — rare on the target audience.
      window.prompt("Copy your share link:", shareUrl);
    }
  }

  return (
    <div className="pv5-spotlight-banner">
      {/* CSS-only confetti — twelve drifting bits */}
      <div className="pv5-confetti" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => (
          <span key={i} className={`pv5-confetti-bit pv5-confetti-${i % 6}`} style={{ left: `${(i * 5.8) % 100}%`, animationDelay: `${(i * 90) % 1800}ms` }} />
        ))}
      </div>
      <div className="hy-container pv5-spotlight-inner">
        <div className="pv5-spotlight-body">
          <div className="pv5-spotlight-emoji" aria-hidden="true">🎉</div>
          <div>
            <div className="pv5-spotlight-h">You're on {props.displayName}'s wall.</div>
            <div className="pv5-spotlight-sub">
              Your tile is glowing right now. Share this link to spotlight it for friends.
            </div>
          </div>
        </div>
        <button type="button" onClick={handleCopy} className="pv5-spotlight-cta">
          {copied ? "Copied ✓" : "Copy share link"}
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          className="pv5-spotlight-close"
          onClick={() => setVisible(false)}
        >×</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goal bar — autoselects a milestone ahead of current supporter count so the
// bar always has somewhere to go. Cheap urgency: visitors hate seeing
// progress bars stop at 87%.
// ---------------------------------------------------------------------------
const GOAL_MILESTONES = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];
function pickGoal(current: number): number {
  for (const m of GOAL_MILESTONES) if (m > current) return m;
  // Past 10k? Round up to the next 5k.
  return Math.ceil((current + 1) / 5000) * 5000;
}

function GoalBar(props: { current: number; goal: number }): JSX.Element {
  const pct = Math.min(100, Math.max(0, (props.current / props.goal) * 100));
  const remaining = Math.max(0, props.goal - props.current);
  return (
    <section className="pv5-goal hy-container">
      <div className="pv5-goal-card">
        <div className="pv5-goal-head">
          <div className="pv5-goal-title">
            <strong>{props.current}</strong> / {props.goal} supporters
          </div>
          <div className="pv5-goal-sub">
            {remaining > 0
              ? `${remaining} to go — help unlock the next milestone.`
              : "Goal smashed. New milestone unlocked."}
          </div>
        </div>
        <div className="pv5-goal-track" role="progressbar"
             aria-valuenow={props.current} aria-valuemin={0} aria-valuemax={props.goal}>
          <div className="pv5-goal-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Find-my-tile search. Case-insensitive contains match; on best match,
// scrolls the wall into view and triggers the SubsWall spotlight effect
// via the parent's onMatch callback.
// ---------------------------------------------------------------------------
function WallSearch(props: { tiles: SubTile[]; onMatch: (id: string | null) => void; inline?: boolean }): JSX.Element {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SubTile[]>([]);

  useEffect(() => {
    const term = q.trim().toLowerCase();
    if (!term) { setHits([]); props.onMatch(null); return; }
    const matches = props.tiles
      .filter((t) => t.name.toLowerCase().includes(term))
      .slice(0, 6);
    setHits(matches);
    if (matches[0]) {
      props.onMatch(matches[0].id);
      // Defer the scroll so the spotlight class is already in DOM.
      window.setTimeout(() => {
        document.getElementById("pv5-wall-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      props.onMatch(null);
    }
  }, [q, props.tiles]);

  return (
    <section className={`pv5-search ${props.inline ? "pv5-search-inline" : "hy-container"}`}>
      <div className="pv5-search-card">
        <span className="pv5-search-icon" aria-hidden="true">⌕</span>
        <input
          type="text"
          className="pv5-search-input"
          placeholder="Find my signature on the wall…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
        />
        {q ? (
          <button type="button" className="pv5-search-clear"
                  onClick={() => setQ("")} aria-label="Clear search">×</button>
        ) : null}
      </div>
      {q && hits.length === 0 ? (
        <div className="pv5-search-empty">No supporter named "{q}" yet.</div>
      ) : null}
      {hits.length > 1 ? (
        <div className="pv5-search-hits">
          {hits.length} matches · showing <strong>{hits[0].name}</strong>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SupporterRow — one supporter card. When the viewer owns the wall, shows
// an inline reply composer so the creator can write a personal thanks
// straight from the public profile. Reply renders on the tile + here.
// ---------------------------------------------------------------------------
function SupporterRow(props: {
  tile: SubTile;
  handle: string;
  isOwner: boolean;
  onReplied: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.tile.creatorReply ?? "");
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      const r = await fetch(`/api/wall/${encodeURIComponent(props.handle)}/reply`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tile_id: props.tile.id, reply: draft.trim() }),
      });
      if (r.ok) {
        setEditing(false);
        props.onReplied();
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="pv5-supporter">
      <div className="pv5-sup-row">
        <div className="pv5-sup-av">{props.tile.name[0]}</div>
        <div className="pv5-sup-body">
          <div className="pv5-sup-name">{props.tile.name}</div>
          <div className="pv5-sup-msg">{props.tile.message}</div>
          {props.tile.creatorReply && !editing ? (
            <div className="pv5-sup-reply">
              <span className="pv5-sup-reply-tag">↳ your reply</span>
              <span className="pv5-sup-reply-text">{props.tile.creatorReply}</span>
            </div>
          ) : null}
        </div>
        <div className="pv5-sup-amt hy-mono">${(props.tile.monthlyCents / 100).toFixed(0)}/mo</div>
      </div>
      {props.isOwner ? (
        editing ? (
          <div className="pv5-sup-replybox">
            <input
              type="text"
              maxLength={160}
              placeholder="Say thanks (≤160 chars)…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            />
            <div className="pv5-sup-replybox-acts">
              <button type="button" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button type="button" onClick={save} disabled={saving} className="pv5-sup-reply-save">
                {saving ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="pv5-sup-replybtn" onClick={() => setEditing(true)}>
            {props.tile.creatorReply ? "Edit reply" : "Reply"}
          </button>
        )
      ) : null}
    </li>
  );
}
